import React, { ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import type { ButtonTypes } from '@core/Components/Buttons/themes'
import { showLogin } from '@core/Areas/Login/helpers/showLogin'
import { closeModal } from '@core/Components/Modal'
import { appHistory } from '@core/Utils/routing/history'
import { OverlayClose, state$ as giddyUpOverlayState$ } from '@core/Components/Overlay'
import { ACCOUNT_ACTIVITY_PENDING_ROUTE } from '@core/Areas/AccountActivity/constants'
import { KAMBI_PENDING_HASH_ROUTE } from '@core/Areas/KambiClient/constants'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { BusyIndicator } from './BusyIndicator'
import {
  QuickbetBetButtonStyled,
  QuickbetButtonMoneyStyled,
  QuickbetButtonStyled,
} from './Common.styles'

export const LoginButton = () => (
  <QuickbetButtonStyled
    type='button'
    onClick={() => {
      showLogin()
    }}
    data-tid-quickbet-login-button
  >
    Log in to place bet
  </QuickbetButtonStyled>
)

export const BettingButton: React.FC<{
  onClick: () => void
  betCost: number
  isDisabled: boolean
  label: string
}> = ({ onClick, label, betCost, isDisabled }) => (
  <QuickbetBetButtonStyled onClick={onClick} disabled={isDisabled} data-tid-quickbet-betting-btn>
    {label} <QuickbetButtonMoneyStyled amount={betCost} />
  </QuickbetBetButtonStyled>
)

export const BusyButton = () => (
  <QuickbetButtonStyled disabled={true}>
    <BusyIndicator />
  </QuickbetButtonStyled>
)

export const EditBetButton: React.FC<{
  isDisabled?: boolean
  onClick: VoidFunction
}> = ({ isDisabled = false, onClick }) => (
  <QuickbetButtonStyled
    color='secondary'
    type='button'
    role='button'
    onClick={onClick}
    disabled={isDisabled}
    data-tid-quickbet-edit-bet-btn
  >
    Edit your Bet
  </QuickbetButtonStyled>
)

export const PendingBetsButton: React.FC<{ isKambiSports: boolean }> = ({ isKambiSports }) => {
  const dispatch = useDispatch()

  const pendingBetsButtonClick = () => {
    dispatch(closeModal({ id: QUICKBET_MODAL_ID }))
    const route = isKambiSports ? KAMBI_PENDING_HASH_ROUTE : '/' + ACCOUNT_ACTIVITY_PENDING_ROUTE
    appHistory.push(route)

    giddyUpOverlayState$.take(1).subscribe(state => state.isOpen && OverlayClose())
  }

  return (
    <QuickbetButtonStyled
      color='secondary'
      type='button'
      onClick={pendingBetsButtonClick}
      data-tid-quickbet-pending-bets-btn
    >
      Pending Bets
    </QuickbetButtonStyled>
  )
}

export const NextEventButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <QuickbetButtonStyled
    color='secondary'
    type='button'
    onClick={onClick}
    data-tid-quickbet-nextevents-btn
  >
    Next Events
  </QuickbetButtonStyled>
)

export const KeepBetsButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <QuickbetButtonStyled type='button' color='secondary' onClick={onClick}>
    Keep Bets
  </QuickbetButtonStyled>
)

export const CloseButton = () => {
  const dispatch = useDispatch()
  return (
    <QuickbetButtonStyled
      type='button'
      onClick={() => dispatch(closeModal({ id: QUICKBET_MODAL_ID }))}
      data-tid-quickbet-close-btn
    >
      Close
    </QuickbetButtonStyled>
  )
}

export const DepositButton: React.FC<{
  color?: Extract<keyof ButtonTypes, 'primary' | 'secondary'>
  children: ReactNode
  onClick?: () => void
  isDisabled?: boolean
}> = ({ isDisabled = false, children, color = 'primary', onClick }) => (
  <QuickbetButtonStyled
    color={color}
    type='button'
    role='button'
    onClick={onClick}
    disabled={isDisabled}
    data-tid-quickbet-deposit-btn
  >
    {children}
  </QuickbetButtonStyled>
)
